import React from 'react'
import { useDispatch } from 'react-redux'
import CheckIcon from '../../img/icon-check.svg'
import QuestionIcon from '../../img/icon-question.svg'
import CloseIcon from '../../img/icon-close-circle.svg'
import LayerInfoImage from '../../img/help/layer-info.png'
import DataBasinIcon from '../../img/logo-databasin.png'
import ExternalLogo from '../../img/external.png'
import CreateLinkImage from '../../img/help/create-link.png'
import Accordion from './Accordion'
import { setDialog as setDialogConnect } from '../actions/navigation'
import { Flags } from 'react-feature-flags'

export default () => {
    const dispatch = useDispatch()
    const setDialog = dialogName => dispatch(setDialogConnect(dialogName))

    return (
        <>
            <div className="content-section">
                <div className="intro-text">
                    <p>
                        Site Check is a free and public mapping tool that helps users quickly find parcels where housing
                        projects may qualify for streamlining and exemptions under the California Environmental Quality
                        Act (CEQA). Developers and public agencies considering how CEQA may apply to a housing project
                        can turn to Site Check as a "first step" in their evaluations.
                    </p>
                    <p>
                        <strong>
                            Users should not construe as legal advice any contents of, or outputs from, Site Check, and
                            should independently verify all determinations.
                        </strong>
                    </p>
                    <p>This online tool can be used on your desktop, tablet, or mobile phone.</p>
                </div>
                <div className="help-accordion">
                    <Accordion
                        content={[
                            {
                                id: 'tool-layout',
                                initiallyClosed: true,
                                heading: <h6 className="title is-6">Tool Layout</h6>,
                                body: (
                                    <>
                                        <p>
                                            The tool has two vertical panels - use the left panel to make your
                                            selections as you toggle between the Explore, Screen, and Analyze tabs. The
                                            panel to the right is the map that will display the results of your
                                            selections.
                                        </p>
                                    </>
                                )
                            },
                            {
                                id: 'layers',
                                initiallyClosed: true,
                                heading: <h6 className="title is-6">Explore</h6>,
                                body: (
                                    <>
                                        <p>
                                            By checking or <u>unchecking</u> boxes next to various spatial data layer
                                            options, you can turn on or turn off spatial data layers such as city
                                            boundaries or transit stops. At the bottom of the left panel, you can toggle
                                            between base (map) layer options.
                                        </p>
                                        <p>
                                            Clicking the button beside each spatial data layer option provides a brief
                                            description and - for most spatial data layer options - allows you to adjust
                                            the layer's transparency.
                                        </p>
                                        <p>
                                            You can also download and examine spatial data layers with the icons.
                                            Clicking on either the blue Data Basin icon{' '}
                                            <img
                                                alt="Databasin"
                                                src={DataBasinIcon}
                                                style={{ height: '20px', verticalAlign: 'middle' }}
                                            />{' '}
                                            or the California State <u>Geoportal</u> icon{' '}
                                            <img
                                                alt="External Link"
                                                src={ExternalLogo}
                                                style={{ height: '20px', verticalAlign: 'middle' }}
                                            />{' '}
                                            will take you to a site on which you can download the spatial data layer.
                                            Data Basin is a public and free mapping and analysis platform and the
                                            California State <u>Geoportal</u> is the State's geographic open data
                                            portal.
                                        </p>
                                        <p>
                                            You can download spatial data layers from Data Basin as GIS{' '}
                                            <u>shapefiles</u>, layer files, or PowerPoint slides. You can also access{' '}
                                            <u>Metadata</u> for each layer.
                                        </p>
                                    </>
                                )
                            },
                            {
                                id: 'screen',
                                initiallyClosed: true,
                                heading: <h6 className="title is-6">Screen</h6>,
                                body: (
                                    <>
                                        {' '}
                                        <p>
                                            The screening function allows you to filter parcels based on whether they
                                            qualify for specific CEQA streamlining options or whether they meet certain
                                            spatial criteria. For example, you can find parcels that are covered by a
                                            specific plan, near transit, or in an area with below average Vehicle Miles
                                            Traveled (VMT). You can use multiple filters at once.
                                        </p>
                                    </>
                                )
                            },
                            {
                                id: 'analyze',
                                initiallyClosed: true,
                                heading: <h6 className="title is-6">Analyze</h6>,
                                body: (
                                    <>
                                        <p>
                                            Select the parcel(s) by typing in the address or Assessor Parcel Number
                                            (APN) into the search box or by directly clicking on the parcel(s) in the
                                            map using your mouse. You can select multiple adjacent parcels by using the
                                            Box or Shape function in the tool. To group multiple parcels into a single
                                            project, click on the check boxes next to the selected parcels you would
                                            like to group together, then click on Add Project. This will create a report
                                            for these parcels as a single project.
                                        </p>
                                        <p>
                                            After you have selected all parcels and projects, click on the Create Report
                                            button to see the potential accelerated pathways to CEQA compliance.{' '}
                                        </p>
                                        <br />
                                        <p>
                                            <strong>
                                                <i>Creating a Report</i>
                                            </strong>
                                        </p>
                                        <p>
                                            If you are using a desktop computer, summarized information about the
                                            selected parcels and projects will appear on the left side. Next to each
                                            parcel, the tool indicates whether and how many CEQA streamlining options
                                            may or may not apply. Next to each parcel or project the tool determined may
                                            qualify for CEQA streamlining, a blue check mark{' '}
                                            <span className="svg-icon svg-primary-primary svg-secondary-white">
                                                <CheckIcon width={20} height={20} />
                                            </span>{' '}
                                            or a green question{' '}
                                            <span className="svg-icon svg-primary-accent svg-secondary-white">
                                                <QuestionIcon width={20} height={20} />
                                            </span>{' '}
                                            will display the number of CEQA streamlining options that may apply to the
                                            parcel or project. Next to each parcel the tool determined will not qualify
                                            for CEQA streamlining, an "X" will appear{' '}
                                            <span className="svg-icon svg-primary-danger-light svg-secondary-white">
                                                <CloseIcon width={20} height={20} />
                                            </span>
                                            .
                                        </p>
                                    </>
                                )
                            },
                            {
                                id: 'create-link',
                                initiallyClosed: true,
                                heading: <h6 className="title is-6">Create Link</h6>,
                                body: (
                                    <>
                                        {' '}
                                        <p>
                                            At any point while using Site Check you can save your work by clicking on
                                            the "Create Link" button in the upper right hand corner to create a unique
                                            URL. You can save the URL by pasting it to your own files or you can share
                                            the URL with others.
                                        </p>
                                        <p className="has-text-centered">
                                            <img
                                                alt="Create link screenshot"
                                                src={CreateLinkImage}
                                                style={{ width: '500px' }}
                                            />
                                        </p>
                                    </>
                                )
                            },
                            {
                                id: 'methods',
                                initiallyClosed: true,
                                heading: <h6 className="title is-6">Methods</h6>,
                                body: (
                                    <>
                                        {' '}
                                        <p>
                                            This{' '}
                                            <span className="pseudo-link" onClick={() => setDialog('methods')}>
                                                pop-up
                                            </span>{' '}
                                            has detailed descriptions of the methodology and data used in the tool to
                                            spatially analyze the various exemptions and requirements in the CEQA
                                            provisions.
                                        </p>
                                    </>
                                )
                            },
                            {
                                id: 'createaccount',
                                initiallyClosed: true,
                                heading: <h6 className="title is-6">How to Create and Use Account</h6>,
                                body: (
                                    <>
                                        {' '}
                                        <p>
                                            You can save and store projects in Site Check by creating an account. First,
                                            click the Sign Up button and follow the prompts to create an account.
                                            Afterward, you can click the Save Project button to save a new project or
                                            update a past project.
                                        </p>
                                        <p>
                                            Click the My Account button to open the option menu. You can refresh the map
                                            by clicking the Start New Project button, or access and manage saved
                                            projects by clicking the Manage Projects button.
                                        </p>
                                    </>
                                )
                            },
                            {
                                id: 'printmap',
                                initiallyClosed: true,
                                heading: <h6 className="title is-6">Print Map</h6>,
                                body: (
                                    <>
                                        {' '}
                                        <p>
                                            To export or print the map on your screen, click the print icon located in
                                            the upper right corner of the tool. For best results, change the layout to
                                            landscape and go to settings to turn on background graphics.
                                        </p>
                                    </>
                                )
                            }
                        ]}
                    />
                </div>
            </div>
        </>
    )
}
